/* istanbul ignore file */

import { useCallback } from 'react';
import { type SubmitErrorHandler, type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { yupResolver } from '@hookform/resolvers/yup';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { usePurchaseAttempt } from '@order/integrations-stripe';

import {
  DeliveryMethods,
  type GiftCardCheckoutFormDefaultValues,
  type GiftCardCheckoutFormFieldRules,
  type GiftCardCheckoutFormReturn,
  type GiftCardCheckoutFormState,
} from '../../gift-card-checkout-form.types';
import { useGiftCardCheckout } from '../useGiftCardCheckout';
import { useGiftCardCheckoutFormValidationSchema } from '../useGiftCardCheckoutFormValidationSchema';
import { giftCardCheckoutFormMessages as messages } from './useGiftCardCheckoutForm.messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Provides Gift Card Checkout form helpers
 */
export const useGiftCardCheckoutForm = (
  options: UseGiftCardCheckoutFormOptions,
): UseGiftCardCheckoutFormReturnType => {
  const { rules, defaultValues } = options;

  const { checkoutGiftCard } = useGiftCardCheckout();
  const { push: createNoticeBanner, remove: removeNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  // ─── Purchase Attempt ────────────────────────────────────────────────

  const { purchaseAttempt, incrementPurchaseAttempt } = usePurchaseAttempt();

  // ─── Validation ──────────────────────────────────────────────────────

  const validationSchema = useGiftCardCheckoutFormValidationSchema({
    rules,
    defaultValues,
  });

  // ─── Form ────────────────────────────────────────────────────────────

  /**
   * @see {@link https://react-hook-form.com/}
   */
  const form = useForm<GiftCardCheckoutFormState>({
    defaultValues: {
      amountVariation: 'standard',
      deliveryDateOption: 'NOW',
      deliveryMethod: DeliveryMethods.Message,
      quantity: 1,
      utcOffset: new Date().getTimezoneOffset() / 60,
      ...defaultValues,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // ─── Helpers | Notice Banners ────────────────────────────────────────

  /**
   * Displays a custom error notice banner with predefined options.
   */
  const showErrorNoticeBanner = useCallback(() => {
    createNoticeBanner(
      {
        id: FORM_ERROR_NOTICE_BANNER_ID,
        autoHideTimeout: FORM_NOTICE_BANNER_AUTO_HIDE_TIMEOUT,
        palette: 'caution',
        text: formatMessage(messages.formSubmissionError),
      },
      true,
    );
  }, [createNoticeBanner, formatMessage]);

  /**
   * Removes a pending error notice banner.
   */
  const removeErrorNoticeBanner = useCallback(() => {
    removeNoticeBanner(FORM_ERROR_NOTICE_BANNER_ID);
  }, [removeNoticeBanner]);

  // ─── Helpers | Form Submission ───────────────────────────────────────

  /**
   * Removes all pending notice banners and submits the checkout form.
   */
  const onSubmit = useCallback<SubmitHandler<GiftCardCheckoutFormState>>(
    async (formData) => {
      // NOTE: Remove any pending error banners
      removeErrorNoticeBanner();

      const {
        deliveryDateTime,
        assetId,
        quantity,
        recipientName,
        recipientEmail,
        message,
        senderName,
        amount,
        deliveryDateOption,
        utcOffset = 0,
        deliveryMethod,
        paymentMethodId,
      } = formData;

      await checkoutGiftCard(
        {
          input: {
            assetId,
            quantity,
            recipientName,
            recipientEmail,
            message,
            senderName,
            paymentMethodId,
            deliveryDateTime,
            utcOffset,
            balance: amount,
            isAsap: deliveryDateOption === 'NOW',
            deliveryMethods: [deliveryMethod],
          },
        },
        incrementPurchaseAttempt,
      );
    },
    [checkoutGiftCard, removeErrorNoticeBanner, incrementPurchaseAttempt],
  );

  /**
   * Handles errors on submission.
   */
  const onSubmitError = useCallback<SubmitErrorHandler<GiftCardCheckoutFormState>>(
    (errors) => {
      incrementPurchaseAttempt();

      const errorValues = Object.values(errors);
      const firstErrorMessage = errorValues?.[0]?.message;

      if (!firstErrorMessage) return;

      showErrorNoticeBanner();
    },
    [showErrorNoticeBanner, incrementPurchaseAttempt],
  );

  /**
   * @see {@link https://react-hook-form.com/docs/useform/handlesubmit}
   */
  const submitForm = form.handleSubmit(onSubmit, onSubmitError);

  // ─────────────────────────────────────────────────────────────────────

  return { purchaseAttempt, incrementPurchaseAttempt, form, submitForm };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const FORM_NOTICE_BANNER_AUTO_HIDE_TIMEOUT = 4000;
const FORM_ERROR_NOTICE_BANNER_ID = 'gift-card-checkout-form-submit-error';

// ─── Types ───────────────────────────────────────────────────────────────────

type UseGiftCardCheckoutFormOptions = {
  rules: GiftCardCheckoutFormFieldRules;
  defaultValues: Partial<GiftCardCheckoutFormDefaultValues>;
};

type UseGiftCardCheckoutFormReturnType = {
  purchaseAttempt: number;
  incrementPurchaseAttempt: () => void;
  form: GiftCardCheckoutFormReturn;
  submitForm: () => Promise<void>;
};
